import { useMutation, useQuery } from '@apollo/client';
import {
  AccountStates,
  AffiliationRelations,
  AffiliationTypes,
  EntityTypes,
  GoalRiskLevels,
  GoalTimeHorizons,
  GoalTypes,
  OrganizationUserAccessTypes,
} from '@generated/graphql';
import type { Account } from '@interfaces/account';
import { getAccountTypes } from '@interfaces/account';
import type { RepCode } from '@interfaces/repCode';
import type { TranslatedString } from '@interfaces/shared';
import { AccountTypes, Jurisdictions } from '@onevesthq/ov-enums';
import { useRespTransferSync } from '@ovComponents/4-module/workflowCompletion/subSteps/respTransfer/useRespTransferSync';
import {
  WorkflowContext,
  type WorkflowContextType,
} from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { UserContext } from '@providers/userContextProvider';
import { delay } from '@utils/common';
import { FlagsmithFlags } from '@utils/flagsmith';
import { useToast } from 'hephaestus/web';
import { first, uniq } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import {
  CLOSE_ACCOUNT,
  CLOSE_SUB_ACCOUNT,
  CREATE_ACCOUNT,
  CREATE_GOAL,
  CREATE_SUB_ACCOUNT,
  FETCH_USER,
  UPDATE_ACCOUNT_AFFILIATIONS,
} from './createAccount.queries';
import { CreateAccountVisual } from './createAccount.visual';

export type ContinueFunctionArgs = {
  accountType: string;
  isTransferredFromAnotherInstitution?: boolean;
};

export type CreateAccountOptionsType = {
  title?: TranslatedString;
  subtitle?: TranslatedString;
  displayAccountDefinitions?: boolean;
  autoAssignIndividualAsDirector?: boolean;
  autoAssignAuthorizedIndividual?: boolean;
  autoAssignBeneficialOwner?: boolean;
  options?: any;
};

const ACCOUNT_JURISDICTION_ALLOW_LIST: {
  [key in AccountTypes]?: Jurisdictions[];
} = {
  [AccountTypes.USA_JT_JTBE]: [
    Jurisdictions.US_AK,
    Jurisdictions.US_AR,
    Jurisdictions.US_DE,
    Jurisdictions.US_FL,
    Jurisdictions.US_HI,
    Jurisdictions.US_IL,
    Jurisdictions.US_IN,
    Jurisdictions.US_KY,
    Jurisdictions.US_MD,
    Jurisdictions.US_MA,
    Jurisdictions.US_MI,
    Jurisdictions.US_MS,
    Jurisdictions.US_MO,
    Jurisdictions.US_NJ,
    Jurisdictions.US_NY,
    Jurisdictions.US_NC,
    Jurisdictions.US_OH,
    Jurisdictions.US_OK,
    Jurisdictions.US_OR,
    Jurisdictions.US_PA,
    Jurisdictions.US_RI,
    Jurisdictions.US_TN,
    Jurisdictions.US_VT,
    Jurisdictions.US_VA,
    Jurisdictions.US_WY,
  ],
  [AccountTypes.USA_JT_CP]: [
    Jurisdictions.US_AZ,
    Jurisdictions.US_CA,
    Jurisdictions.US_ID,
    Jurisdictions.US_LA,
    Jurisdictions.US_NM,
    Jurisdictions.US_NV,
    Jurisdictions.US_TX,
    Jurisdictions.US_WA,
    Jurisdictions.US_WI,
  ],
};

const isUserEighteenBeforeBeginningOfTheYear = (
  dateOfBirthString: string,
): boolean => {
  const date = new Date(dateOfBirthString);
  const ageUserWillBeThisYear = new Date().getFullYear() - date.getFullYear();
  const ageLastYear = ageUserWillBeThisYear - 1;
  return ageLastYear >= 18;
};

export const CreateAccount = ({
  options,
  userId,
  onNext,
  stepLoading,
  workflowCompletion,
}: {
  options: CreateAccountOptionsType;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: any;
}) => {
  const { t } = useTranslation('workflowCompletions');
  const { workflowData, setWorkflowData } = useContext(
    WorkflowContext,
  ) as WorkflowContextType;
  const {
    activeOrganization,
    userContext,
    activeEntity,
    organizationFeatureFlags,
  } = useContext(UserContext);
  const { enqueueToast } = useToast();
  const isRespInstitutionTransferEnabled = useFeatureFlag(
    FlagsmithFlags.resp_transferred_from_another_institution,
  );

  const [availableAccountTypes, setAvailableAccountTypes] = useState<
    AccountTypes[]
  >([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [selectedRepCode, setSelectedRepCode] = useState<RepCode | undefined>(
    undefined,
  );
  const [repCodeError, setRepCodeError] = useState<boolean>(false);

  const showRepCodeSelect =
    userContext.accessType === OrganizationUserAccessTypes.RepCode;

  const activeEntityAccounts = userContext.entities?.find(
    (e) => e.entity.id === activeEntity?.id,
  )?.accounts;

  const {
    data: userData,
    loading: userLoading,
    refetch: refetchUserData,
  } = useQuery(FETCH_USER, {
    variables: { userId },
    fetchPolicy: 'no-cache',
  });

  const [updateAccountAffiliations] = useMutation(UPDATE_ACCOUNT_AFFILIATIONS);
  const { syncRespTransferSelection } = useRespTransferSync();

  const [closeSubAccount] = useMutation(CLOSE_SUB_ACCOUNT, {
    variables: {
      input: {
        subAccountId: workflowData.currentSubAccountId,
        inactiveReason: 'OTHER',
      },
    },
    onCompleted: () => {
      setWorkflowData((prev) => ({
        ...prev,
        createdAccounts: prev.createdAccounts?.filter(
          (account: any) => account.id !== prev.currentAccountId,
        ),
        createdSubAccountIds: prev.createdSubAccountIds?.filter(
          (subAccount: any) => subAccount !== prev.currentSubAccountId,
        ),
      }));
    },
  });

  const [closeAccount] = useMutation(CLOSE_ACCOUNT, {
    variables: {
      input: {
        accountId: workflowData.currentAccountId,
        inactiveReason: 'OTHER',
      },
    },
    onCompleted: async () => {
      await closeSubAccount();
    },
  });

  const [createSubAccountMutation, { loading: createSubAccountLoading }] =
    useMutation(CREATE_SUB_ACCOUNT);

  const [createAccountMutation, { loading: createAccountLoading }] =
    useMutation(CREATE_ACCOUNT);

  const [createGoal, { loading: goalLoading }] = useMutation(CREATE_GOAL, {
    variables: {
      input: {
        userId,
        type: GoalTypes.Other,
        timeHorizon: GoalTimeHorizons.VeryLongTerm,
        riskQuestion1: GoalRiskLevels.Level_5,
      },
    },
  });
  const initializeGoal = async () => {
    const { data } = await createGoal();
    await setWorkflowData((workflowData) => ({
      ...workflowData,
      createdGoalIds: [
        ...(workflowData.createdGoalIds || []),
        data.createGoal.goal.id,
      ],
      currentGoalId: data.createGoal.goal.id,
    }));
    return data.createGoal.goal.id;
  };

  const userType = userData?.fetchUser.user?.type;
  const userJurisdiction = userData?.fetchUser.user?.physicalAddress
    ?.jurisdiction as Jurisdictions;

  useEffect(() => {
    // types allowed by feature flags & by country
    const allAccountTypes = getAccountTypes(
      userType,
      Array.from(organizationFeatureFlags),
      activeOrganization.applicableLocalization?.countries,
    );

    // drop types out of client's jurisdiction
    const availableTypes = allAccountTypes.filter((type) => {
      if (!userJurisdiction) return true;
      const allowList = ACCOUNT_JURISDICTION_ALLOW_LIST[type];
      if (!allowList) return true;
      if (allowList.includes(userJurisdiction)) return true;
      return false;
    });

    // set the available account types
    setAvailableAccountTypes(availableTypes);

    // auto-select when there's only one type
    if (availableTypes.length === 1 && !workflowData.currentAccountType) {
      setWorkflowData((workflowData) => ({
        ...workflowData,
        currentAccountType: availableTypes[0],
      }));
    }
  }, [
    activeOrganization,
    userType,
    userJurisdiction,
    workflowData.currentAccountType,
    setWorkflowData,
  ]);

  const autoAssignAffiliations = (account: Account) => {
    if (account.affiliations && account.affiliations.length > 0) {
      return;
    }
    const contextIndividuals =
      (userContext?.entities ?? []).filter(
        (e) => e.entity.type === EntityTypes.Individual,
      ) ?? [];
    if (
      uniq(contextIndividuals.map((entityObj: any) => entityObj?.entity?.id))
        .length !== 1
    ) {
      /* TODO: should be contextIndividuals.length !== 1, but currently we are experiencing a known
        bug which is entities getting duplicated after newly added
      */
      return;
    }
    const individual = first(contextIndividuals)?.entity;
    const affiliations: any[] = [];
    if (individual) {
      if (options.autoAssignAuthorizedIndividual) {
        affiliations.push({
          relation: AffiliationRelations.Other,
          type: AffiliationTypes.AuthorizedIndividual,
          userId: individual.id,
        });
      }
      if (options.autoAssignIndividualAsDirector) {
        affiliations.push({
          relation: AffiliationRelations.Other,
          type: AffiliationTypes.Director,
          userId: individual.id,
        });
      }
      if (options.autoAssignBeneficialOwner) {
        affiliations.push({
          relation: AffiliationRelations.Other,
          type: AffiliationTypes.BeneficialOwner,
          userId: individual.id,
          allocation: 100,
        });
      }
      if (account.type === AccountTypes.RESP_ADULT) {
        affiliations.push({
          relation: AffiliationRelations.Other,
          type: AffiliationTypes.PrimaryBeneficiary,
          userId: individual.id,
          allocation: 100,
        });
      }
    }
    if (affiliations.length === 0) {
      return;
    }
    updateAccountAffiliations({
      variables: {
        input: { accountId: account.id, affiliations },
      },
      onCompleted: (data: any) => {
        // if entity is incomplete there will be incompleteAffiliations and incompleteFields and affiliations will not be added
        if (
          data?.updateAffiliations?.incompleteAffiliations &&
          data?.updateAffiliations?.incompleteAffiliations?.length > 0
        ) {
          enqueueToast(
            t('corporateCashAccountCreation.incompleteAffiliationError'),
            { variant: 'error' },
          );
        }
      },
    });
  };

  const findAccountById = (accountId?: string | null): Account | undefined =>
    (userData?.fetchUser?.user?.accounts ?? []).find(
      (account: Account) => account.id === accountId,
    );

  /**
   * Returns the persisted transfer flag of the in-progress RESP account that would be
   * reused for the given type, so the dialog can pre-fill the toggle with the value
   * already in the DB rather than always defaulting to false.
   */
  const getReusableRespTransferValue = (selectedType: string): boolean => {
    if (!workflowData.currentAccountId) return false;
    if (selectedType !== workflowData.currentAccountType) return false;
    return (
      findAccountById(workflowData.currentAccountId)
        ?.isTransferredFromAnotherInstitution ?? false
    );
  };

  const executeCreateAccountAndSubAccountFlow = async (
    accountType: string,
    goalId: string,
    isTransferredFromAnotherInstitution = false,
  ) => {
    const { data: accountData } = await createAccountMutation({
      variables: {
        input: {
          userId,
          type: accountType,
          applyForGovFunds: accountType.includes('RESP')
            ? ['BASIC_CESG']
            : undefined,
          isTransferredFromAnotherInstitution:
            isRespInstitutionTransferEnabled && accountType.includes('RESP')
              ? isTransferredFromAnotherInstitution
              : undefined,
          householdClientGroupId:
            workflowData.currentHouseholdClientGroupId ?? undefined,
          ...(showRepCodeSelect &&
            selectedRepCode?.code && {
              repCode: selectedRepCode.code,
            }),
        },
      },
    });

    if (!accountData?.createAccount) {
      return;
    }

    const isRepCodeUser =
      userContext.accessType === OrganizationUserAccessTypes.RepCode;
    const isEntityUser =
      userContext.accessType === OrganizationUserAccessTypes.Entity;
    if (
      isRepCodeUser ||
      (isEntityUser && activeEntityAccounts && activeEntityAccounts.length > 0)
    ) {
      await delay(1000);
    }

    const { data: subAccountData } = await createSubAccountMutation({
      variables: {
        input: {
          accountId: accountData.createAccount.account.id,
          accountType,
          goalId,
          userId,
        },
      },
    });

    if (!subAccountData?.createSubAccount) {
      return;
    }

    if (
      accountType === AccountTypes.CORPORATE_CASH &&
      (options.autoAssignAuthorizedIndividual ||
        options.autoAssignBeneficialOwner ||
        options.autoAssignIndividualAsDirector)
    ) {
      autoAssignAffiliations(
        subAccountData.createSubAccount.subAccount.account,
      );
    }

    const newAccount = subAccountData.createSubAccount.subAccount.account;
    await setWorkflowData((prev) => ({
      ...prev,
      createdAccounts: prev.createdAccounts?.some(
        (account: any) => account.id === newAccount.id,
      )
        ? prev.createdAccounts
        : [
            ...(prev.createdAccounts || []),
            {
              id: newAccount.id,
              type: newAccount.type,
              state: newAccount.state,
            },
          ],
      createdSubAccountIds: [
        ...(prev.createdSubAccountIds || []),
        subAccountData.createSubAccount.subAccount.id,
      ],
      currentSubAccountId: subAccountData.createSubAccount.subAccount.id,
      currentAccountId: newAccount.id,
      currentAccountState: newAccount.state,
      currentAccountType: newAccount.type,
    }));

    await onNext();
  };

  const onContinue = async ({
    accountType,
    isTransferredFromAnotherInstitution = false,
  }: ContinueFunctionArgs) => {
    if (
      accountType === 'RESP_ADULT' &&
      !isUserEighteenBeforeBeginningOfTheYear(
        userData.fetchUser.user.dateOfBirth,
      )
    ) {
      enqueueToast(t('respAccountCreation.respAgeRestrictionError'), {
        variant: 'error',
      });
      return;
    }

    if (showRepCodeSelect && !selectedRepCode?.code) {
      setRepCodeError(true);
      return;
    }
    setRepCodeError(false);
    setSaving(true);

    const goalId = workflowData.currentGoalId ?? (await initializeGoal());

    try {
      if (!workflowData.currentAccountId) {
        await executeCreateAccountAndSubAccountFlow(
          accountType,
          goalId,
          isTransferredFromAnotherInstitution,
        );
      } else if (
        workflowData.currentAccountId &&
        workflowData.currentAccountState === AccountStates.Initiated &&
        workflowData.currentAccountType !== accountType
      ) {
        // It should close original account if a new type is selected
        // Because we are creating a 'phantom' goal here - the goal should stay the same
        await closeAccount();
        await executeCreateAccountAndSubAccountFlow(
          accountType,
          goalId,
          isTransferredFromAnotherInstitution,
        );
      } else {
        const reusedAccount = findAccountById(workflowData.currentAccountId);
        // Refetch after a real write so the no-cache account snapshot isn't stale.
        if (
          reusedAccount &&
          (await syncRespTransferSelection(
            reusedAccount,
            isTransferredFromAnotherInstitution,
          ))
        ) {
          await refetchUserData();
        }
        await onNext();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <CreateAccountVisual
      options={options}
      availableAccountTypes={availableAccountTypes}
      accountType={workflowData.currentAccountType ?? ''}
      getReusableRespTransferValue={getReusableRespTransferValue}
      continueFunc={onContinue}
      loading={
        createSubAccountLoading ||
        createAccountLoading ||
        goalLoading ||
        userLoading ||
        stepLoading ||
        saving
      }
      workflowCompletion={workflowCompletion}
      saving={saving}
      selectedRepCode={selectedRepCode}
      setSelectedRepCode={showRepCodeSelect ? setSelectedRepCode : undefined}
      repCodeError={repCodeError}
      setRepCodeError={showRepCodeSelect ? setRepCodeError : undefined}
    />
  );
};

export default CreateAccount;
